import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetCertificateUploadListDataQuery,
  useUpdateCertificateUploadTableHeadDataMutation,
} from "../../../store/queries/Master";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/CertificateUpload/certificateUploadSlice";

const useCertificateUpload = () => {
  const paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
  ];

  const dispatch = useDispatch();

  const { tableFields, showEditModal, currentBranch } = useSelector(
    (state) => state.global,
  );
  const certificateUploadState = useSelector(
    (state) => state.certificateUpload,
  );

  const branchId = currentBranch?.[0]?._id;

  const {
    data: certificateUploadList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetCertificateUploadListDataQuery({
    page_size: certificateUploadState.currentPageSize,
    page: certificateUploadState.currentPage,
    sort_by: certificateUploadState.sortBy,
    sort_order: certificateUploadState.sortOrder,
    search: certificateUploadState.search,
    branch_id: branchId,
  });

  const [updateCertificateUploadFields] =
    useUpdateCertificateUploadTableHeadDataMutation();

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = certificateUploadList?.data?.fields;
      }),
    );
    // eslint-disable-next-line
  }, [certificateUploadList]);

  useEffect(() => {
    if (certificateUploadState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        }),
      );
    }
    // eslint-disable-next-line
  }, [certificateUploadState.clearSelection]);

  const tableFieldsMemo = useMemo(() => tableFields, [tableFields]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      }),
    );
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
      }),
    );
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      }),
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      }),
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      }),
    );
  };

  const handleSort = (label) => {
    if (certificateUploadState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder =
            certificateUploadState.sortOrder === "asc" ? "desc" : "asc";
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        }),
      );
    }
  };

  const handleBulkUploadClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showBulkUpload = true;
      }),
    );
  };

  const handleBulkUploadClose = () => {
    dispatch(
      updateConfig((state) => {
        state.showBulkUpload = false;
      }),
    );
  };

  const isImageField = (field) => {
    if (!field) return false;
    const f = String(field).toLowerCase();
    return (
      f.includes("image") ||
      f.includes("photo") ||
      f.includes("file") ||
      f === "thumb" ||
      f === "thumbnail"
    );
  };

  const getRow = (field, data) => {
    const value = data?.[field];
    if (isImageField(field) && typeof value === "string" && value) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="pro-d-inline-block"
        >
          <img
            src={value}
            alt={field}
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: 4,
              border: "1px solid #e4e7ec",
            }}
          />
        </a>
      );
    }
    return <p className="pro-mb-0">{value ?? ""}</p>;
  };

  return {
    certificateUploadState,
    certificateUploadList,
    isLoading,
    isFetching,
    refetch,
    tableFields: tableFieldsMemo,
    showEditModal,
    paginationOptions,
    updateCertificateUploadFields,
    getRow,
    handleSearch,
    handleClearClick,
    handleEditColumnsClick,
    handlePagination,
    handlePageSize,
    handleSort,
    handleBulkUploadClick,
    handleBulkUploadClose,
  };
};

export default useCertificateUpload;
